import {Box} from 'src/design/atoms/Box';
import {Center} from 'src/design/atoms/Center';
import {Grid} from 'src/design/atoms/Grid';
import {Loader} from 'src/design/atoms/Loader';
import {Stack} from 'src/design/atoms/Stack';
import React, {useCallback, useState} from 'react';
import {useWatch} from 'react-hook-form';
import type {DocumentResponse} from 'src/api/ProactApiClient';
import {
  useCreateDocumentNodeMutation,
  useGetDocumentQuery,
  useUpdateDocumentMutation,
} from 'src/redux/queries/document';
import {BackBar} from 'src/app/layout/BackBar';
import {PageHeader} from 'src/design/organisms/PageHeader';
import {SectionCard} from 'src/design/atoms/SectionCard';
import {toFormDataV2} from 'src/app/forms/engine/formData';
import {ProactField} from 'src/app/forms/engine/ProactField';
import {ProactForm} from 'src/app/forms/engine/ProactForm';
import {useEntitySyncedForm} from 'src/app/forms/engine/useEntitySyncedForm';
import {FormFileUploadV2} from 'src/app/forms/FormFileDropzone/FormFileUploadV2';
import {SidebarLinkDocumentManager} from 'src/app/layout/Sidebar/SidebarLinksConfig';
import {
  FileMeta,
  MapDocumentResponseToDocumentCreateRequest,
  MapDocumentResponseToDocumentUpdateRequest,
} from 'src/pages/Modules/DocumentManagement/documentHelpers';
import {PathUtility} from 'src/shared/constants/PathBuilder';
import {useAlert} from 'src/shared/hooks/useAlert';
import {useDocumentAncestors} from 'src/shared/hooks/useDocumentAncestors';
import {useDocumentManagementBreadcrumbs} from 'src/shared/hooks/useDocumentManagementBreadcrumbs';
import {parseErrorMessage} from 'src/shared/utils/parseErrorMessage';
import type {IFormFieldV2} from 'src/types/FormV2';
import {useBackNavigation} from 'src/shared/hooks/useBackNavigation';
import {useAppNavigate} from 'src/shared/hooks/useAppNavigate';

export interface DocumentFileFormProps {
  id?: number;
  parentId?: string;
  mode: 'create' | 'edit';
}

const fileField = FileMeta.find(f => f.name === 'attachmentURL')!;

const FILE_DETAILS_FORM_META: IFormFieldV2[] = [
  {
    name: 'name',
    label: 'File name',
    type: 'text',
    required: true,
  },
  {
    name: 'summaryDescription',
    label: 'Summary',
    type: 'text',
    required: true,
    validation: {maxLength: 1000},
    textboxOptions: {multiline: true},
  },
];

export const DocumentFileForm: React.FC<DocumentFileFormProps> = ({
  id,
  parentId,
  mode,
}) => {
  const navigate = useAppNavigate();
  const {setAlert} = useAlert();

  const [attachmentURL, setAttachmentURL] = useState('');
  const [attachmentError, setAttachmentError] = useState<string>();
  const [isSavePending, setIsSavePending] = useState(false);

  const isCreateMode = mode === 'create';
  const isEditMode = mode === 'edit';

  const {data: fetchedDocument, isLoading} = useGetDocumentQuery(
    {id: id!},
    {skip: id === undefined}
  );

  const [createDocumentNode] = useCreateDocumentNodeMutation();
  const [updateDocument] = useUpdateDocumentMutation();

  const form = useEntitySyncedForm({
    formMeta: FILE_DETAILS_FORM_META,
    entity: fetchedDocument,
    onEntityArrival: entity => setAttachmentURL(entity.attachmentURL ?? ''),
    toFormData: toFormDataV2,
  });

  const handleAttachmentChange = useCallback((_: string, value: string) => {
    setAttachmentURL(value ?? '');
    if (value) {
      setAttachmentError(undefined);
    }
  }, []);

  const handleSave = async () => {
    setIsSavePending(true);
    try {
      const values = form.methods.getValues();
      if (isCreateMode) {
        const model = MapDocumentResponseToDocumentCreateRequest({
          ...values,
          attachmentURL,
        } as DocumentResponse);
        model.isFolder = false;
        model.parentId = parentId !== '0' ? Number(parentId) : undefined;
        const response = await createDocumentNode(model).unwrap();
        setAlert(`${response.name} was successfully created`, 'success');
        navigate(PathUtility.DocumentManagerViewFile(response.id, 'content'), {
          replace: true,
        });
      } else {
        const model = MapDocumentResponseToDocumentUpdateRequest({
          ...values,
          attachmentURL,
          id,
          version: fetchedDocument?.version,
        } as DocumentResponse);
        const response = await updateDocument(model).unwrap();
        setAlert(`${response.name} was successfully updated`, 'success');
        navigate(PathUtility.DocumentManagerViewFile(response.id, 'content'), {
          replace: true,
        });
      }
    } catch (error) {
      setAlert(parseErrorMessage(error), 'error');
    }
    setIsSavePending(false);
  };

  const handleValidateAndSave = async () => {
    const isDetailsValid = await form.validateForm();
    const hasAttachment = !!attachmentURL;
    setAttachmentError(
      hasAttachment ? undefined : 'A file attachment is required'
    );

    if (isDetailsValid && hasAttachment) {
      void handleSave();
    }
  };

  const {goBack: handleCancel} = useBackNavigation(
    isEditMode && id
      ? PathUtility.DocumentManagerViewFile(id, 'content')
      : PathUtility.DocumentsManagerList(
          parentId && parentId !== '0' ? Number(parentId) : 0
        )
  );

  const ancestors = useDocumentAncestors(id || parentId, parentId);

  const name = useWatch({control: form.methods.control, name: 'name'});

  const {breadcrumbs, fallbackTo} = useDocumentManagementBreadcrumbs({
    mode: isCreateMode ? 'create-document' : 'edit-document',
    nodeId: id,
    parentId: parentId,
    nodeName: name as string,
    ancestors,
  });

  if (isLoading) {
    return (
      <Center py="xl">
        <Loader size="lg" />
      </Center>
    );
  }

  return (
    <Stack gap="lg">
      <BackBar fallbackTo={fallbackTo} breadcrumbs={breadcrumbs} />

      <PageHeader>
        <PageHeader.Title icon={SidebarLinkDocumentManager.icon}>
          {isCreateMode ? 'New Document' : 'Edit File'}
        </PageHeader.Title>
        {isEditMode && (
          <PageHeader.Badges>
            <PageHeader.Badge color="yellow" variant="light">
              Editing
            </PageHeader.Badge>
          </PageHeader.Badges>
        )}
        <PageHeader.Actions>
          <PageHeader.Action variant="cancel" onClick={handleCancel}>
            Cancel
          </PageHeader.Action>
          <PageHeader.Action
            variant={isCreateMode ? 'create' : 'save'}
            onClick={handleValidateAndSave}
            loading={isSavePending}>
            {isCreateMode ? 'Create' : 'Save Changes'}
          </PageHeader.Action>
        </PageHeader.Actions>
      </PageHeader>

      <ProactForm form={form}>
        <SectionCard title="Details" data-testid="section-details">
          <Box p="md">
            <Grid gutter="md">
              <ProactField name="name" />
              <ProactField name="summaryDescription" />
            </Grid>
          </Box>
        </SectionCard>
      </ProactForm>

      <SectionCard title="Attachment" data-testid="section-attachment">
        <Box p="md">
          <FormFileUploadV2
            field={fileField}
            value={attachmentURL || null}
            error={attachmentError}
            onChange={handleAttachmentChange}
          />
        </Box>
      </SectionCard>
    </Stack>
  );
};
